import type { Branded } from './branded'
import type { EntryKind } from './entry'
import type { IssueKind, IssueStatus, Priority } from './issue'
import type { PlanStatus } from './plan'
import type { ProjectId } from './project'

const SHARE_KIND = {
	ISSUE: 'issue',
	PLAN: 'plan',
} as const

type ShareKind = (typeof SHARE_KIND)[keyof typeof SHARE_KIND]

type SharedIssue = {
	readonly kind: IssueKind
	readonly title: string
	readonly body: string
	readonly status: IssueStatus
	readonly priority: Priority
	readonly tags: readonly string[]
	readonly externalRef: string
	readonly updatedAt: Date
}

type SharedPlan = {
	readonly title: string
	readonly goal: string
	readonly status: PlanStatus
	readonly tags: readonly string[]
	readonly progress: { readonly total: number; readonly done: number }
	readonly updatedAt: Date
}

type SharedEntry = {
	readonly id: string
	readonly kind: EntryKind
	readonly title: string
	readonly body: string
	readonly createdAt: Date
}

type SharedItem =
	| {
			readonly kind: typeof SHARE_KIND.ISSUE
			readonly label: string
			readonly issue: SharedIssue
			readonly tickets: readonly (SharedIssue & { readonly id: string })[]
			readonly plans: readonly (SharedPlan & { readonly id: string })[]
			readonly journal: readonly SharedEntry[]
			readonly docs: readonly SharedEntry[]
	  }
	| {
			readonly kind: typeof SHARE_KIND.PLAN
			readonly label: string
			readonly plan: SharedPlan
			readonly tickets: readonly (SharedIssue & { readonly id: string })[]
	  }

type ShareId = Branded<string, 'ShareId'>

const shareId = (value: string): ShareId => value as ShareId

type ShareTarget = {
	readonly kind: ShareKind
	readonly id: string
	readonly projectId: ProjectId
}

type ShareLink = {
	readonly id: ShareId
	readonly label: string
	readonly token: string
	readonly createdAt: Date
	readonly lastAccessedAt: Date | undefined
}

const SHARE_LABEL_MAX = 120

const shareUrl = (origin: string, token: string): string => `${origin}/share/${token}`

export { SHARE_KIND, SHARE_LABEL_MAX, shareId, shareUrl }
export type { SharedEntry, SharedIssue, SharedItem, SharedPlan, ShareId, ShareKind, ShareLink, ShareTarget }
